import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { supportedLanguages } from '../supportedLanguages';

// Le texte vit dans public/locales/<lang>/privacy.md, comme la documentation
// et « À propos » : un texte juridique se relit et se traduit mieux d'un bloc
// que découpé en clés de translation.json.
//
// La date de mise à jour est écrite dans le texte, et non calculée : c'est la
// date à laquelle la politique a changé, pas celle du jour où on la lit.
//
// La page défile normalement. Elle tenait auparavant dans une zone de hauteur
// fixe qui défilait seule, sans barre visible : le texte paraissait s'arrêter
// à la section 3.

const markdownComponents = {
  h1: ({ node, ...props }: any) => (
    <h1 className="text-2xl font-bold text-amber-900 mb-2" {...props} />
  ),
  h2: ({ node, ...props }: any) => (
    <h2 className="text-xl font-semibold text-amber-900 mt-8 mb-3" {...props} />
  ),
  h3: ({ node, ...props }: any) => (
    <h3 className="text-lg font-medium text-amber-900 mt-5 mb-2" {...props} />
  ),
  p: ({ node, ...props }: any) => (
    <p className="text-amber-900 mb-3 leading-relaxed" {...props} />
  ),
  ul: ({ node, ...props }: any) => (
    <ul className="list-disc pl-6 mb-3 text-amber-900 space-y-1" {...props} />
  ),
  strong: ({ node, ...props }: any) => (
    <strong className="font-semibold text-amber-950" {...props} />
  ),
  a: ({ node, href = '', children, ...props }: any) => (
    <a
      href={href}
      className="text-amber-700 hover:text-amber-900 underline"
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      {...props}
    >
      {children}
    </a>
  ),
};

const PrivacyPolicy = () => {
  const { lang } = useParams();
  const language = lang && supportedLanguages.includes(lang) ? lang : 'fr';

  const { t, i18n } = useTranslation('');
  React.useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  const [content, setContent] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        let res = await fetch(`/locales/${language}/privacy.md`);
        if (!res.ok && language !== 'fr') {
          res = await fetch('/locales/fr/privacy.md');
        }
        const text = res.ok ? await res.text() : '';
        if (!cancelled) setContent(text);
      } catch (error) {
        console.error('Error loading privacy policy:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [language]);

  const canonicalUrl = `https://bible4.me/${language}/privacy`;

  return (
    <>
      <Helmet>
        <title>{t('privacy.title')} — Bible4Me</title>
        <meta name="description" content={t('seo.privacy.description')} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="min-h-screen bg-amber-100 p-4 md:p-8">
        <article className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 md:p-10">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-900"></div>
            </div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {content}
            </ReactMarkdown>
          )}
        </article>
      </div>
    </>
  );
};

export default PrivacyPolicy;
